/**
 * TensorFlow.js バックエンドを 1 回だけ初期化（WebGPU → WebGL → CPU）。
 * personDetection / facePrivacy の両方から共有する。
 */
import * as tf from "@tensorflow/tfjs-core";
import "@tensorflow/tfjs-backend-webgpu";
import "@tensorflow/tfjs-backend-webgl";
import "@tensorflow/tfjs-backend-cpu";

import { devConfig } from "./appConfig.js";

/** @type {Promise<string> | null} */
let backendReadyPromise = null;

/** @type {string | null} */
let resolvedBackendName = null;

const DEFAULT_BACKEND_ORDER = ["webgpu", "webgl", "cpu"];

/**
 * @param {string} name
 * @returns {Promise<string>}
 */
async function activateBackend(name) {
  await tf.setBackend(name);
  await tf.ready();
  const active = tf.getBackend();
  if (active !== name) {
    throw new Error(`backend mismatch: requested ${name}, active ${active}`);
  }
  return active;
}

/**
 * @returns {string[]}
 */
function backendTryOrder() {
  const order = devConfig.tfBackend?.preferOrder;
  if (Array.isArray(order) && order.length > 0) {
    return order.filter((n) => typeof n === "string");
  }
  if (devConfig.tfBackend?.tryWebGpu === false) {
    return ["webgl", "cpu"];
  }
  return DEFAULT_BACKEND_ORDER;
}

/**
 * 利用可能なバックエンドを順に試す。成功した名前を返す。
 * @returns {Promise<string>}
 */
export function ensureTfBackend() {
  backendReadyPromise ??= (async () => {
    const tried = backendTryOrder();
    /** @type {unknown} */
    let lastErr = null;
    for (const name of tried) {
      try {
        resolvedBackendName = await activateBackend(name);
        console.info(`[tf] backend: ${resolvedBackendName}`);
        return resolvedBackendName;
      } catch (err) {
        lastErr = err;
        console.warn(`[tf] backend "${name}" unavailable:`, err);
      }
    }
    throw lastErr instanceof Error
      ? lastErr
      : new Error("No TensorFlow.js backend available");
  })();
  return backendReadyPromise;
}

/** @returns {string | null} */
export function getTfBackendName() {
  return resolvedBackendName ?? tf.getBackend() ?? null;
}

/**
 * テスト・再初期化用（通常は呼ばない）
 */
export function resetTfBackendForTests() {
  backendReadyPromise = null;
  resolvedBackendName = null;
}
